// LAP-767 — Carte « Recommandation » de la Synthèse Professionnelle, alignée
// sur les autres conseillers IA (finance / temps / écologie) :
//   • un chip de score RÉEL (note d'employabilité 0-100 calculée par
//     buildProAdvisor, mêmes règles que la page Conseiller Pro / Formation),
//   • un accès direct « Voir l'analyse complète » vers /conseiller-pro,
//   • l'état vide et le raccourci `function/function_link` historiques sont
//     conservés (cohérence avec l'existant).
//
// La recommandation elle-même n'est plus une simple carte statique : elle
// devient la porte d'entrée du Conseiller Pro / Formation, miroir de la
// ConseilsCard écologie (LAP-766) côté tableau de bord.
import { Box, Button, Card, Chip } from '@mui/material'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next' // LAP-733 (GE022)
import WTypography from '../../../../Components/whiteTypography'
import { isEmpty } from 'lodash'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import OpenInFullIcon from '@mui/icons-material/OpenInFull'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
// LAP-767 — Score professionnel RÉEL (mêmes règles que la page Conseiller
// Pro / Formation), pour afficher la note d'employabilité dans l'en-tête.
import { buildProAdvisor } from '../../../ConseillerPro/utils/buildProAdvisor'
import { fetchProAdvisorSources } from '../../../ConseillerPro/utils/fetchProAdvisorSources'

const PRO_BLUE = '#5b9bff'

// LAP-767 — Couleur du chip de score, alignée sur les conseillers (≥70 vert,
// ≥45 ambre, sinon rouge ; gris si non évalué).
function scoreChipColors(score) {
  if (score == null) return { bg: 'rgba(148,163,184,0.18)', fg: '#94a3b8' }
  if (score >= 70) return { bg: 'rgba(74,222,128,0.18)', fg: '#4ade80' }
  if (score >= 45) return { bg: 'rgba(251,191,36,0.18)', fg: '#fbbf24' }
  return { bg: 'rgba(248,113,113,0.18)', fg: '#f87171' }
}

function Recommandation({ oldData, updateReports }) {
  const { t } = useTranslation('layout') // LAP-733 (GE022)
  const navigate = useNavigate()

  // LAP-767 — Score d'employabilité RÉEL (note 0-100 de l'analyse complète),
  // récupéré une fois au montage. S'appuie sur le profil, les compétences, les
  // soft skills, la formation et les opportunités, comme la page Conseiller
  // Pro / Formation. Repli silencieux si l'agrégat n'est pas disponible.
  const [realScore, setRealScore] = useState(null)
  // LAP-767 v2 — Courte recommandation DÉRIVÉE de l'analyse (1re reco narrative
  // calculée sans appel LLM par buildProAdvisor), affichée quand aucune
  // recommandation n'est persistée. Évite la carte « texte par défaut » figée :
  // miroir de la ConseilsCard écologie qui montre toujours un conseil concret.
  const [leadReco, setLeadReco] = useState(null)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const sources = await fetchProAdvisorSources()
        const built = buildProAdvisor({ ...sources, t })
        if (!cancelled) {
          setRealScore(built?.proScore?.global ?? null)
          const reco = built?.recommendation || built?.recommendations?.[0] || null
          setLeadReco(reco)
        }
      } catch (_) {
        /* silencieux — la carte reste fonctionnelle sans le score */
      }
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const score = realScore

  return (
    <Card className="dashboard_card__root1" sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box className="dashboard_card__header1">
        <Box display="flex" alignItems="center" gap={1}>
          <AutoAwesomeIcon sx={{ color: PRO_BLUE, fontSize: 22 }} />
          <WTypography component={'span'} variant="h6">
            {t('professionnelle.recommandation') /* LAP-733 (GE022) */}
          </WTypography>
        </Box>
        {score != null && (
          <Chip
            label={t('conseillerPro.scoreChip', {
              score,
              defaultValue: `Score ${score}/100`,
            })}
            size="small"
            sx={{
              bgcolor: scoreChipColors(score).bg,
              color: scoreChipColors(score).fg,
              fontWeight: 700,
              height: 22,
            }}
          />
        )}
      </Box>

      <Box mt={4} minWidth={3} mb={3} flexGrow={1}>
        {isEmpty(oldData) ? (
          // LAP-767 v2 — Si l'analyse fournit une recommandation dérivée, on
          // l'affiche (conseil concret) plutôt que le texte d'incitation figé.
          // Repli sur le texte par défaut tant que l'agrégat n'est pas chargé.
          <Box
            className="no-drag"
            height={'70%'}
            justifyContent={'center'}
            color={'white'}
            display={'flex'}
            flexDirection={'column'}
            alignItems={'center'}
          >
            {leadReco ? (
              <>
                <Box
                  padding={'0 20px'}
                  textAlign={'center'}
                  fontStyle={'italic'}
                  sx={{ lineHeight: 1.6 }}
                >
                  {leadReco}
                </Box>
                <Box
                  padding={'0 20px'}
                  display={'flex'}
                  width={'100%'}
                  justifyContent={'flex-end'}
                  alignItems={'center'}
                  gap={0.75}
                  mt={3}
                  sx={{ color: '#dbe7ff', fontWeight: 600, fontSize: 13 }}
                >
                  <AutoAwesomeIcon sx={{ color: PRO_BLUE, fontSize: 16 }} />
                  {t('conseillerPro.lifeAgent', 'Life Agent')}
                </Box>
              </>
            ) : (
              <>
                <Box padding={'0 20px'} textAlign={'center'} fontWeight={'800'}>
                  {t('professionnelle.recommandationDesc') /* LAP-733 (GE022) */}
                </Box>
                <Box padding={'0 20px'} display={'flex'} width={'100%'} justifyContent={'end'} mt={5}>
                  {t('professionnelle.openAi') /* LAP-733 (GE022) */}
                </Box>
              </>
            )}
          </Box>
        ) : (
          <>
            <Box
              height={'50%'}
              justifyContent={'center'}
              color={'white'}
              display={'flex'}
              flexDirection={'column'}
              fontSize={'20px'}
            >
              <Box textAlign={'center'} fontStyle={'italic'}>
                {oldData?.content}
              </Box>
              <Box textAlign={'right'} mt={5}>
                {oldData?.source}
              </Box>
            </Box>
            {oldData?.function && (
              <Box color={'white'} textAlign={'center'} mt={8} className="no-drag">
                {oldData?.function}
                <Box
                  sx={{ cursor: 'pointer' }}
                  component={'span'}
                  ml={2}
                  onClick={() => navigate(oldData.function_link)}
                >
                  <KeyboardArrowRightIcon sx={{ bgcolor: '#438295', borderRadius: '50%' }} />
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>

      {/* LAP-767 — Accès à l'analyse pro / formation complète (score, détail par
          dimension, diagnostic, recommandations, chat) — miroir des conseillers
          patrimonial / temps / écologie. Disponible même sans recommandation
          persistée : la page calcule tout à partir des données du profil. */}
      <Button
        fullWidth
        className="no-drag"
        onClick={() => navigate('/conseiller-pro')}
        startIcon={<OpenInFullIcon />}
        sx={{
          mt: 'auto',
          mb: 1,
          color: '#dbe7ff',
          fontWeight: 600,
          textTransform: 'none',
          border: '1px solid rgba(91,155,255,0.4)',
          bgcolor: 'rgba(91,155,255,0.16)',
          py: 0.75,
          '&:hover': { bgcolor: 'rgba(91,155,255,0.28)', borderColor: PRO_BLUE },
        }}
      >
        {t('conseillerPro.viewFullAnalysis', "Voir l'analyse complète")}
      </Button>
    </Card>
  )
}

export default Recommandation
